import { useState } from 'react'
import { useAtom } from 'jotai'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import ListItemIcon from '@mui/material/ListItemIcon'
import IconButton from '@mui/material/IconButton'
import Alert from '@mui/material/Alert'
import CloseRoundedIcon from "@mui/icons-material/CloseRounded"
import Box from '@mui/material/Box'

import type { CollectionPointCategoryListFilters, CreateCategoryListExportResponse, CreateHouseholdMembersExportResponse } from '@/types'
import { householdMembersExportIdAtom, householdsExportIdAtom } from '@/state'
import XLSIcon from '@/components/attachments/xls-icon'
import useMenu from '@/hooks/use-menu'
import { useCreateCategoryListExport, useCreateHouseholdMembersExport } from '@/hooks/api/use-export'
import HouseholdsExportDownload from './households-export-download'
import HouseholdMembersExportDownload from './household-members-export-download'

export default function ExportMenu({
    year,
    motivationModelId,
    filters,
}: {
    year: number,
    motivationModelId: number,
    filters: CollectionPointCategoryListFilters,
}) {
    const [householdsExportId, setHouseholdsExportId] = useAtom(householdsExportIdAtom)
    const [householdMembersExportId, setHouseholdMembersExport] = useAtom(householdMembersExportIdAtom)
    const [requestInProgress, setRequestInProgress] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    const {
        anchorEl,
        open,
        handleOpen,
        handleClose,
    } = useMenu()

    const successCallbackHouseholds = (data: CreateCategoryListExportResponse) => {
        if (data.id) {
            setHouseholdsExportId(data.id)
        }
    }

    const successCallbackHouseholdMembers = (data: CreateHouseholdMembersExportResponse) => {
        if (data.id) {
            setHouseholdMembersExport(data.id)
        }
    }

    const mutationExportHouseholds = useCreateCategoryListExport(
        year,
        motivationModelId,
        {
            onSuccessCallback: successCallbackHouseholds,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            },
            setRequestInProgress,
        },
    )

    const mutationExportHouseholdMembers = useCreateHouseholdMembersExport(
        year,
        {
            onSuccessCallback: successCallbackHouseholdMembers,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            },
            setRequestInProgress,
        },
    )

    const handleClickExportHouseholds = () => {
        setRequestInProgress(true)
        setError(null)
        mutationExportHouseholds.mutate(filters)
        handleClose()
    }

    const handleClickExportHouseholdMembers = () => {
        setRequestInProgress(true)
        setError(null)
        mutationExportHouseholdMembers.mutate()
        handleClose()
    }

    return (
        <>
            <Button
                color="terciary"
                startIcon={<XLSIcon />}
                onClick={handleOpen}
                disabled={requestInProgress}
                id="export-menu-households-button"
                aria-controls={open ? 'export-menu-households' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                sx={{
                    backgroundColor: 'customGrey.boxBackgroundLight',
                    '&:hover': {
                        backgroundColor: 'customLightGreen.main',
                    },
                }}
            >
                Export
            </Button>
            <Menu
                id="export-menu-households"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'export-menu-households-button',
                }}
                disableScrollLock
            >
                <MenuItem
                    onClick={handleClickExportHouseholds}
                >
                    <ListItemIcon>
                        <XLSIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Exportovat svozová místa</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={handleClickExportHouseholdMembers}
                >
                    <ListItemIcon>
                        <XLSIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Exportovat poplatníky</ListItemText>
                </MenuItem>
            </Menu>
            {householdsExportId &&
                <HouseholdsExportDownload
                    exportId={householdsExportId}
                    handleClose={() => setHouseholdsExportId(null)}
                />
            }
            {householdMembersExportId &&
                <HouseholdMembersExportDownload
                    exportId={householdMembersExportId}
                    handleClose={() => setHouseholdMembersExport(null)}
                />
            }
            {error &&
                <Box sx={{
                    flexGrow: 1,
                    width: '100%',
                    pb: 0.5,
                }}>
                    <Alert
                        severity="error"
                        sx={(theme) => ({
                            flexGrow: 1,
                            margin: theme.spacing(1, 0),
                        })}
                        action={
                            <IconButton
                                color="error"
                                size="small"
                                onClick={() => {
                                    setError(null)
                                }}
                            >
                                <CloseRoundedIcon />
                            </IconButton>
                        }
                    >
                        {error}
                    </Alert>
                </Box>
            }
        </>
    )
}

